import {
  LibraryPublishCreate,
  LibraryPublishRequest,
  LibraryPublishResponse,
  LibraryPublishStatus,
} from '~/api/sylvanus/typedefs.autogen';
import { isStandardAiLibraryModel } from '~/features/x-ray/library/isStandardAiLibraryModel';
import { LibraryModelVersion, Queries } from '~/features/x-ray/library/types';
import { intlService } from '~/i18n/IntlService';
import { sylvanus } from '~/services/serviceInstances';

import { withQueryFormat } from './searchScopeUtils';

interface RequestParams {
  models: LibraryModelVersion[];
  scopes: Queries;
  isDraft: boolean;
}

export const publishLibraryModels = async ({
  models,
  scopes,
  isDraft,
}: RequestParams): Promise<LibraryModelVersion[]> => {
  // Omit as_stdai_models. That flag is Pilot client-provisioning and needs extra
  // cdif/uf fields; without it Sylvanus loads Standard AI details from Pilot.
  const request: LibraryPublishRequest = {
    publishes: models.map((model) => toPublishCreate(model, scopes, isDraft)),
  };

  const response = (await sylvanus.post(
    '/library/model/publish',
    request,
  )) as LibraryPublishResponse;

  const publishesById = new Map(
    response.publishes.map((modelPublish) => [
      modelPublish.library_model_id,
      modelPublish,
    ]),
  );

  return models.map((model) =>
    mapPublishStatusToLibraryModelVersion(
      requirePublishStatus(publishesById.get(model.libraryModelId)),
      model,
    ),
  );
};

const toPublishCreate = (
  model: LibraryModelVersion,
  scopes: Queries,
  isDraft: boolean,
): LibraryPublishCreate => {
  const query = scopes[model.libraryModelId];

  return {
    library_model_id: model.libraryModelId,
    library_model_version_number: model.version,
    draft: isDraft,
    config_overrides:
      isStandardAiLibraryModel(model) || !model.config.scope
        ? null
        : {
            scope: {
              data: {
                attributes: {
                  filters: [],
                  query: withQueryFormat(query ?? []),
                  store_as_recent_search: false,
                },
              },
              type: 'document',
            },
          },
  };
};

const requirePublishStatus = (modelPublish?: LibraryPublishStatus) => {
  if (!modelPublish) {
    throw new Error(
      intlService.getIntl().formatMessage({
        id: 'publishLibraryModels.modelNotFound',
        defaultMessage: 'Published model not found',
        description: 'Error message when published model is not found',
      }),
    );
  }

  return modelPublish;
};

const mapPublishStatusToLibraryModelVersion = (
  modelPublish: LibraryPublishStatus,
  model: LibraryModelVersion,
): LibraryModelVersion => {
  return {
    config: model.config,
    createdDate: model.createdDate,
    description: model.description,
    label: model.label,
    latest: model.latest,
    libraryModelId: modelPublish.library_model_id,
    promptModelId: modelPublish.prompt_model_id ?? null,
    promptModelPublishStatus: model.promptModelPublishStatus,
    publishErrorMessage: modelPublish.error_message ?? undefined,
    publishState: modelPublish.state,
    stdaiKey: model.stdaiKey,
    tags: model.tags,
    targetEntityDetails: model.targetEntityDetails,
    version: modelPublish.library_model_version_number,
  };
};
